/**
 * Полезная площадь полки (useful_area_pct) — разметка контуром на кропе полки.
 *
 * Полезная площадь — объективная вместимость: где на полке ФИЗИЧЕСКИ может лежать товар,
 * если выложить его как можно больше. Без скошенных углов полигона, решёток и дверей
 * холодильника. Модель отдаёт площадь товара в долях того же кропа, поэтому
 * сырое / полезная площадь — доля от максимально возможного в абсолютных п.п.; норму
 * задаёт порог, а не эта площадь. Стеллаж расширяют редко, поэтому число стабильное.
 *
 * Два режима, потому что иногда проще обвести лишнее, чем нужное:
 *  - «Обвести полезное» — полезно то, что внутри контуров;
 *  - «Обвести лишнее» — полезно всё, кроме того, что внутри контуров.
 * Контуры объединяются: перекрытие не считается дважды. Хранятся в долях кропа, поэтому
 * разметка ложится на любой кадр той же полки, пока не сдвинули её полигон (тогда флаг
 * «разметить заново»). Число под курсором — подсказка; сохраняется то, что посчитал сервер.
 *
 * Пришло на смену «конструктору максимума» с перцентилями сырого (p01/p50/p98): тот
 * подбирал делитель по наблюдениям, то есть по типичной выкладке, а не по вместимости.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR from 'swr';
import { Info, Loader2, MousePointer2, PenLine, Redo2, Save, Trash2, TriangleAlert, Undo2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { coverageOf, hitTest, nearestVertex, type Contour } from '@/pages/Admin/datasets/shelfMaskGeometry';
import {
  getCalibrationShelfReference,
  saveShelfUsefulArea,
  type CalibrationShelfCandidate,
  type FracPoint,
  type UsefulAreaMode,
} from '@/api/calibrationShelf';

/** Окно, из которого берутся кадры полки. Неделя — кадры со снимком есть почти всегда. */
const DAYS = 7;
/** Сетка для подсчёта площади на клиенте: контуры в долях × GRID. */
const GRID = 1000;
/** Радиус захвата вершины и «замыкания» контура по первой точке, в долях ширины кропа. */
const GRAB = 0.03;

const TAG_TITLE: Record<CalibrationShelfCandidate['tag'], string> = {
  high: 'Самая полная',
  mid: 'Типичная',
  low: 'Почти пустая',
};
// Самая полная первой: на ней лучше видно, куда товар вообще кладут.
const TAG_ORDER: CalibrationShelfCandidate['tag'][] = ['high', 'mid', 'low'];

const MODE_HINT: Record<UsefulAreaMode, string> = {
  include: 'Обведите область, где может лежать товар. Всё вне контуров не считается.',
  exclude: 'Обведите то, что нужно убрать: углы полигона, решётки, дверь, ценники. Остальное — полезная площадь.',
};

const pct1 = (v: number) => `${(Math.round(v * 1000) / 10).toLocaleString('ru-RU')}%`;
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const at = (iso: string) => new Date(iso).toLocaleString('ru-RU', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
});

type Tool = 'pen' | 'cursor';

export default function ShelfUsefulAreaDialog(
  { shelfId, open, onOpenChange, onApplied }: {
    shelfId: number;
    open: boolean;
    onOpenChange: (v: boolean) => void;
    onApplied?: () => void;
  },
) {
  const { data, isLoading, error, mutate } = useSWR(
    open ? ['calib-useful-area', shelfId] : null,
    () => getCalibrationShelfReference(shelfId, DAYS).then(r => r.data),
    { revalidateOnFocus: false },
  );

  const [frameTag, setFrameTag] = useState<CalibrationShelfCandidate['tag'] | null>(null);
  const [mode, setMode] = useState<UsefulAreaMode>('include');
  const [polys, setPolysRaw] = useState<FracPoint[][]>([]);
  const [draft, setDraft] = useState<FracPoint[]>([]);
  const [tool, setTool] = useState<Tool>('pen');
  const [selected, setSelected] = useState(-1);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const past = useRef<FracPoint[][][]>([]);
  const future = useRef<FracPoint[][][]>([]);
  const drag = useRef<{ poly: number; vertex: number } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  const candidates = useMemo(
    () => [...(data?.candidates ?? [])].sort((a, b) => TAG_ORDER.indexOf(a.tag) - TAG_ORDER.indexOf(b.tag)),
    [data],
  );
  const frame = candidates.find(c => c.tag === frameTag) ?? candidates[0];

  // Открыть на сохранённой разметке; без неё — чистый лист в режиме «полезное».
  useEffect(() => {
    if (!data) return;
    const m = data.useful_area_markup;
    setMode(m?.mode ?? 'include');
    setPolysRaw(m?.polygons ?? []);
    setDraft([]);
    setSelected(-1);
    setDirty(false);
    past.current = []; future.current = [];
  }, [data]);

  useEffect(() => { setSize(null); }, [frame?.crop_url]);

  const setPolys = useCallback((next: (p: FracPoint[][]) => FracPoint[][], remember = true) => {
    setPolysRaw(prev => {
      if (remember) { past.current.push(prev); future.current = []; }
      return next(prev);
    });
    setDirty(true);
  }, []);

  const undo = useCallback(() => {
    if (draft.length) { setDraft(d => d.slice(0, -1)); return; }
    setPolysRaw(prev => {
      const last = past.current.pop();
      if (!last) return prev;
      future.current.push(prev);
      return last;
    });
    setSelected(-1);
    setDirty(true);
  }, [draft.length]);

  const redo = useCallback(() => {
    setPolysRaw(prev => {
      const next = future.current.pop();
      if (!next) return prev;
      past.current.push(prev);
      return next;
    });
    setSelected(-1);
    setDirty(true);
  }, []);

  /** Доля кропа под контурами и полезная доля — на клиенте, для числа под курсором. */
  const drawn = useMemo(
    () => coverageOf(polys.map(p => p.map(([x, y]) => [x * GRID, y * GRID]) as Contour), GRID, GRID) / 100,
    [polys],
  );
  const useful = polys.length ? (mode === 'include' ? drawn : 1 - drawn) : null;

  // Пропорции кропа нужны, чтобы «радиус» захвата был круглым на экране, а не по долям.
  const aspect = size ? size.h / size.w : 1;
  const W = 1000;
  const H = Math.round(W * aspect);

  const toFrac = useCallback((e: { clientX: number; clientY: number }): FracPoint => {
    const svg = svgRef.current;
    if (!svg) return [0, 0];
    const r = svg.getBoundingClientRect();
    return [clamp01((e.clientX - r.left) / r.width), clamp01((e.clientY - r.top) / r.height)];
  }, []);

  /** В координатах экрана-viewBox (W×H), где расстояния честные по обеим осям. */
  const scaled = useCallback((p: FracPoint[]): Contour => p.map(([x, y]) => [x * W, y * H]) as Contour, [H]);

  const closeDraft = useCallback(() => {
    if (draft.length >= 3) {
      const d = draft;
      setPolys(prev => [...prev, d]);
    }
    setDraft([]);
  }, [draft, setPolys]);

  const removeSelected = useCallback(() => {
    if (selected < 0) return;
    const i = selected;
    setPolys(prev => prev.filter((_, k) => k !== i));
    setSelected(-1);
  }, [selected, setPolys]);

  const onDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const p = toFrac(e);
    const [sx, sy] = [p[0] * W, p[1] * H];
    if (tool === 'pen') {
      // Клик рядом с первой точкой замыкает контур — как в разметке полигонов.
      if (draft.length >= 3 && nearestVertex(scaled([draft[0]]), sx, sy, GRAB * W) === 0) {
        closeDraft();
        return;
      }
      setDraft(d => [...d, p]);
      return;
    }
    if (selected >= 0) {
      const v = nearestVertex(scaled(polys[selected]), sx, sy, GRAB * W);
      if (v >= 0) {
        past.current.push(polys.map(c => c.slice()));
        future.current = [];
        drag.current = { poly: selected, vertex: v };
        return;
      }
    }
    setSelected(hitTest(polys.map(scaled), sx, sy));
  }, [tool, draft, polys, selected, toFrac, scaled, closeDraft, H]);

  const onMove = useCallback((e: React.MouseEvent) => {
    if (!drag.current) return;
    const p = toFrac(e);
    const { poly, vertex } = drag.current;
    setPolys(prev => {
      const next = prev.map(c => c.slice());
      next[poly][vertex] = p;
      return next;
    }, false);
  }, [toFrac, setPolys]);

  const endDrag = useCallback(() => { drag.current = null; }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'Enter' && draft.length) {
        e.preventDefault(); closeDraft();
      } else if (e.key === 'Backspace' && draft.length) {
        e.preventDefault(); setDraft(d => d.slice(0, -1));
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selected >= 0) {
        e.preventDefault(); removeSelected();
      } else if (e.key === 'Escape' && (draft.length || selected >= 0)) {
        // Esc сначала сбрасывает черновик/выделение, и только потом закрывает окно.
        e.preventDefault(); e.stopPropagation(); setDraft([]); setSelected(-1);
      } else if (e.key === 'z' || e.key === 'я') {
        e.preventDefault(); undo();
      } else if (e.key === 'y' || e.key === 'н') {
        e.preventDefault(); redo();
      } else if (e.key === 'p' || e.key === 'з') {
        setTool('pen'); setSelected(-1);
      } else if (e.key === 'v' || e.key === 'м') {
        setTool('cursor'); setDraft([]);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, draft.length, selected, closeDraft, removeSelected, undo, redo]);

  const save = async (clear = false) => {
    if (!data) return;
    setSaving(true);
    setNote(null);
    try {
      const r = await saveShelfUsefulArea({
        shelf_id: shelfId, mode, polygons: clear ? [] : polys,
        frame_id: frame?.frame_id ?? null, backfill_days: DAYS,
      });
      const d = r.data;
      setNote({
        ok: true,
        text: d.value == null
          ? 'Разметка снята'
          : `Полезная площадь ${pct1(d.value)} сохранена`
            + (d.divisor !== d.previous_divisor
              ? `; делитель ${pct1(d.previous_divisor)} → ${pct1(d.divisor)}`
                + (d.rescaled_rows ? `, пересчитано ${d.rescaled_rows} замеров` : '')
                + (d.backfill_blocked_before ? ' (сутки с отправленным отчётом не трогали)' : '')
              : ''),
      });
      await mutate();
      onApplied?.();
    } catch (e) {
      const detail = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setNote({ ok: false, text: detail || 'Не удалось сохранить разметку' });
    } finally {
      setSaving(false);
    }
  };

  const header = data
    ? `Полезная площадь — ${[data.shelf.rack_type, data.shelf.rack_name].filter(Boolean).join(' · ')} / ${data.shelf.name}`
    : 'Полезная площадь полки';
  const t = data?.thresholds;
  const stroke = Math.max(2, W / 400);
  const dimId = `useful-dim-${shelfId}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} widthClassName="max-w-6xl">
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{header}</DialogTitle>
        </DialogHeader>

        <div className="mb-3 flex gap-2 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-100">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <b>Полезная площадь</b> — где на полке физически может лежать товар, если выложить
            его как можно больше. Углы полигона, решётки, дверь холодильника сюда не входят.
            Это не норма выкладки: норму задаёт порог. Выкладка считается как доля от полезной
            площади, в абсолютных п.п.
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Загружаем кадры полки…
          </div>
        )}
        {error && !data && <ServerStatusNotice variant="card" error={error} onRetry={() => mutate()} />}

        {data && (
          <>
            {data.useful_area_stale && (
              <div className="mb-3 flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  Контур полки изменили после разметки — кроп теперь другой. Проверьте контуры на
                  свежем кадре и сохраните заново.
                </div>
              </div>
            )}

            {/* Кадры полки: на каком рисовать. Разметка в долях кропа, поэтому одна на все. */}
            {candidates.length > 1 && (
              <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                <span className="text-muted-foreground">кадр</span>
                {candidates.map(c => (
                  <button
                    key={c.tag}
                    type="button"
                    onClick={() => setFrameTag(c.tag)}
                    className={`flex items-center gap-2 rounded-md border p-1 pr-2 ${
                      c === frame ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground'}`}
                  >
                    {c.crop_url && <img src={c.crop_url} alt="" className="h-8 w-14 rounded object-contain" />}
                    <span>{TAG_TITLE[c.tag]}</span>
                    <span className="text-muted-foreground">{at(c.at)}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="mb-2 flex flex-wrap items-center gap-2">
              <ToggleGroup value={mode} onChange={(v) => { if (v) { setMode(v as UsefulAreaMode); setDirty(true); } }}>
                <ToggleGroupItem value="include">Обвести полезное</ToggleGroupItem>
                <ToggleGroupItem value="exclude">Обвести лишнее</ToggleGroupItem>
              </ToggleGroup>
              <Button size="sm" variant={tool === 'pen' ? 'default' : 'outline'} onClick={() => { setTool('pen'); setSelected(-1); }} title="P">
                <PenLine className="mr-1.5 h-4 w-4" /> Рисовать
              </Button>
              <Button size="sm" variant={tool === 'cursor' ? 'default' : 'outline'} onClick={() => { setTool('cursor'); setDraft([]); }} title="V">
                <MousePointer2 className="mr-1.5 h-4 w-4" /> Править
              </Button>
              <Button size="sm" variant="outline" disabled={selected < 0} onClick={removeSelected} title="Del">
                <Trash2 className="mr-1.5 h-4 w-4" /> Удалить контур
              </Button>
              <Button size="sm" variant="ghost" onClick={undo} title="Z"><Undo2 className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" onClick={redo} title="Y"><Redo2 className="h-4 w-4" /></Button>
            </div>
            <div className="mb-2 text-xs text-muted-foreground">
              {MODE_HINT[mode]}{' '}
              {tool === 'pen'
                ? 'Клик — точка; клик по первой точке, двойной клик или Enter — замкнуть; Backspace — убрать точку; Esc — отменить.'
                : 'Клик по контуру — выбрать; тяните за точки; Del — удалить контур.'}
            </div>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
              <div className="relative select-none self-start overflow-hidden rounded-lg border border-border bg-muted">
                {frame?.crop_url ? (
                  <>
                    <img
                      src={frame.crop_url}
                      alt=""
                      className="block w-full"
                      draggable={false}
                      onLoad={(e) => setSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                    />
                    {size && (
                      <svg
                        ref={svgRef}
                        viewBox={`0 0 ${W} ${H}`}
                        preserveAspectRatio="none"
                        className={`absolute inset-0 h-full w-full ${tool === 'pen' ? 'cursor-crosshair' : 'cursor-pointer'}`}
                        onMouseDown={onDown}
                        onMouseMove={onMove}
                        onMouseUp={endDrag}
                        onMouseLeave={endDrag}
                        onDoubleClick={closeDraft}
                      >
                        {/* «Полезное»: всё вне контуров притемнено — видно, что останется. */}
                        {mode === 'include' && polys.length > 0 && (
                          <>
                            <defs>
                              <mask id={dimId}>
                                <rect x={0} y={0} width={W} height={H} fill="white" />
                                {polys.map((p, i) => (
                                  <polygon key={i} points={scaled(p).map(q => q.join(',')).join(' ')} fill="black" />
                                ))}
                              </mask>
                            </defs>
                            <rect x={0} y={0} width={W} height={H} fill="rgba(0,0,0,.55)" mask={`url(#${dimId})`} />
                          </>
                        )}
                        {polys.map((p, i) => (
                          <polygon
                            key={i}
                            points={scaled(p).map(q => q.join(',')).join(' ')}
                            fill={i === selected ? 'rgba(59,130,246,.35)'
                              : mode === 'include' ? 'rgba(34,197,94,.15)' : 'rgba(239,68,68,.45)'}
                            stroke={i === selected ? '#1d4ed8' : mode === 'include' ? '#15803d' : '#b91c1c'}
                            strokeWidth={stroke}
                          />
                        ))}
                        {selected >= 0 && polys[selected] && scaled(polys[selected]).map(([x, y], i) => (
                          <circle key={i} cx={x} cy={y} r={Math.max(5, W / 160)} fill="#fff" stroke="#1d4ed8" strokeWidth={stroke} />
                        ))}
                        {draft.length > 0 && (
                          <>
                            <polyline points={scaled(draft).map(q => q.join(',')).join(' ')}
                              fill="rgba(234,179,8,.25)" stroke="#a16207" strokeWidth={stroke} />
                            {scaled(draft).map(([x, y], i) => (
                              <circle key={i} cx={x} cy={y} r={i === 0 ? Math.max(7, W / 110) : Math.max(4, W / 220)}
                                fill={i === 0 ? '#fde047' : '#a16207'} stroke="#a16207" strokeWidth={stroke / 2} />
                            ))}
                          </>
                        )}
                      </svg>
                    )}
                  </>
                ) : (
                  <div className="flex h-48 items-center justify-center p-4 text-center text-sm text-muted-foreground">
                    За неделю нет ни одного кадра этой полки со снимком — размечать не на чем.
                  </div>
                )}
              </div>

              <div className="space-y-3 text-sm">
                <div className="rounded-lg border border-border p-3">
                  <div className="text-xs text-muted-foreground">Полезная площадь</div>
                  <div className="text-3xl font-semibold tabular-nums">{useful == null ? '—' : pct1(useful)}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {mode === 'include' ? 'обведено' : 'убрано'}{' '}
                    <span className="tabular-nums text-foreground">{pct1(drawn)}</span> кропа · контуров {polys.length}
                  </div>
                  {dirty && <div className="mt-1 text-xs text-amber-600">не сохранено</div>}
                </div>
                <div className="space-y-1 rounded-lg border border-border p-3 text-xs text-muted-foreground">
                  <div>
                    сохранено{' '}
                    <span className="font-semibold text-foreground tabular-nums">
                      {t?.useful_area_pct == null ? 'нет' : pct1(t.useful_area_pct)}
                    </span>
                    {data.useful_area_markup && (
                      <> · {data.useful_area_markup.marked_by}, {at(data.useful_area_markup.marked_at)}</>
                    )}
                  </div>
                  <div>
                    поправка модели{' '}
                    <span className="tabular-nums text-foreground">
                      {t?.model_correction_max_pct == null ? 'нет' : pct1(t.model_correction_max_pct)}
                    </span>
                  </div>
                  <div>
                    делитель сейчас <b className="tabular-nums text-foreground">{t ? pct1(t.divisor) : '—'}</b>
                  </div>
                  {t?.model_correction_max_pct != null && (
                    <div className="pt-1">
                      Пока задана поправка модели, делит она — полезная площадь сохранится, но
                      делитель не сменится.
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Button disabled={saving || polys.length === 0 || draft.length > 0} onClick={() => save()}>
                    {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
                    Сохранить
                  </Button>
                  {data.useful_area_markup && (
                    <Button variant="outline" disabled={saving} onClick={() => save(true)}>
                      <X className="mr-1.5 h-4 w-4" /> Снять разметку
                    </Button>
                  )}
                </div>
                {note && (
                  <div className={`text-sm ${note.ok ? 'text-emerald-600' : 'text-destructive'}`}>{note.text}</div>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
